import React, { useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { MapPin, Clock, Car, Calculator, RotateCcw, Navigation } from 'lucide-react';
import { toast } from 'sonner';

const TravelTimeCalculatorPage = () => {
  const [fromAddress, setFromAddress] = useState('');
  const [toAddress, setToAddress] = useState('');
  const [travelMode, setTravelMode] = useState('driving');
  const [departureTime, setDepartureTime] = useState('now');
  const [customTime, setCustomTime] = useState('');
  const [result, setResult] = useState(null);
  const [recentCalculations, setRecentCalculations] = useState([
    { id: '1', from: '1234 Healthcare Blvd, Houston TX', to: '5678 Oak St, Houston TX', distance: '12.4 mi', duration: '25 min', date: '01/26/2026' },
    { id: '2', from: '1234 Healthcare Blvd, Houston TX', to: '9012 Pine Ave, Sugar Land TX', distance: '18.7 mi', duration: '35 min', date: '01/26/2026' },
    { id: '3', from: '5678 Oak St, Houston TX', to: '3456 Elm Dr, Katy TX', distance: '22.1 mi', duration: '42 min', date: '01/25/2026' },
  ]);

  const handleCalculate = () => {
    if (!fromAddress || !toAddress) {
      toast.error('Please enter both addresses');
      return;
    }

    // Simulate calculation
    const mockDistance = (Math.random() * 30 + 5).toFixed(1);
    const mockDuration = Math.round(parseFloat(mockDistance) * 2.5);
    
    const newResult = {
      from: fromAddress,
      to: toAddress,
      distance: `${mockDistance} mi`,
      duration: `${mockDuration} min`,
      mode: travelMode,
      trafficCondition: 'Moderate',
      estimatedArrival: new Date(Date.now() + mockDuration * 60000).toLocaleTimeString()
    };

    setResult(newResult);
    setRecentCalculations(prev => [
      { ...newResult, id: Date.now().toString(), date: new Date().toLocaleDateString() },
      ...prev.slice(0, 9)
    ]);
    toast.success('Travel time calculated');
  };

  const handleSwap = () => {
    const temp = fromAddress;
    setFromAddress(toAddress);
    setToAddress(temp);
  };

  const handleClear = () => {
    setFromAddress('');
    setToAddress('');
    setResult(null);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="travel-time-calculator-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-[#3498db] rounded-lg flex items-center justify-center">
            <Car className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Travel Time Calculator</h1>
            <p className="text-gray-600">Calculate travel time and distance between locations</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calculator */}
          <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Calculate Route</h3>
            
            <div className="space-y-4">
              {/* From Address */}
              <div className="relative">
                <Label>From Address</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-green-500" />
                  <Input
                    value={fromAddress}
                    onChange={(e) => setFromAddress(e.target.value)}
                    placeholder="Enter starting address..."
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Swap Button */}
              <div className="flex justify-center">
                <button onClick={handleSwap} className="p-2 hover:bg-gray-100 rounded-full transition-colors" title="Swap addresses">
                  <RotateCcw className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* To Address */}
              <div>
                <Label>To Address</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                  <Input
                    value={toAddress}
                    onChange={(e) => setToAddress(e.target.value)}
                    placeholder="Enter destination address..."
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Options */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Travel Mode</Label>
                  <select
                    value={travelMode}
                    onChange={(e) => setTravelMode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="driving">Driving</option>
                    <option value="transit">Public Transit</option>
                    <option value="walking">Walking</option>
                    <option value="bicycling">Bicycling</option>
                  </select>
                </div>
                <div>
                  <Label>Departure Time</Label>
                  <select
                    value={departureTime}
                    onChange={(e) => setDepartureTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="now">Leave Now</option>
                    <option value="custom">Custom Time</option>
                  </select>
                </div>
              </div>

              {departureTime === 'custom' && (
                <div>
                  <Label>Select Time</Label>
                  <Input
                    type="datetime-local"
                    value={customTime}
                    onChange={(e) => setCustomTime(e.target.value)}
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button onClick={handleCalculate} className="flex-1 bg-[#3498db] hover:bg-[#2980b9]">
                  <Calculator className="w-4 h-4 mr-2" /> Calculate
                </Button>
                <Button onClick={handleClear} variant="outline">
                  Clear
                </Button>
              </div>
            </div>

            {/* Result */}
            {result && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-3">Route Information</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-blue-600 uppercase">Distance</p>
                    <p className="text-xl font-bold text-blue-900">{result.distance}</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-600 uppercase">Duration</p>
                    <p className="text-xl font-bold text-blue-900">{result.duration}</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-600 uppercase">Traffic</p>
                    <p className="text-lg font-semibold text-blue-900">{result.trafficCondition}</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-600 uppercase">ETA</p>
                    <p className="text-lg font-semibold text-blue-900">{result.estimatedArrival}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-sm text-blue-700"><strong>From:</strong> {result.from}</p>
                  <p className="text-sm text-blue-700"><strong>To:</strong> {result.to}</p>
                </div>
              </div>
            )}
          </div>

          {/* Recent Calculations */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Recent Calculations</h3>
            <div className="space-y-3">
              {recentCalculations.map((calc) => (
                <div key={calc.id} className="p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 cursor-pointer" onClick={() => { setFromAddress(calc.from); setToAddress(calc.to); }}>
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs text-gray-500">{calc.date}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">{calc.distance}</span>
                      <span className="text-sm text-gray-600">•</span>
                      <span className="text-sm font-semibold text-blue-600">{calc.duration}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 truncate"><Navigation className="w-3 h-3 inline mr-1 text-green-500" />{calc.from}</p>
                  <p className="text-xs text-gray-600 truncate"><Navigation className="w-3 h-3 inline mr-1 text-red-500" />{calc.to}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TravelTimeCalculatorPage;
